import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/AuthContext";

/**
 * AuthCallback — synchronous catcher for the Emergent Google OAuth redirect.
 * Reads session_id from window.location.hash, posts it to our backend,
 * stores the resulting app JWT, then cleans the URL and re-renders the app
 * (which will now see an authenticated user and route to dashboard).
 */
export default function AuthCallback() {
  const { loginWithGoogleSession } = useAuth();
  const [status, setStatus] = useState("processing"); // processing | error
  const [errMsg, setErrMsg] = useState("");
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setStatus("error");
      setErrMsg("No session ID found in the redirect URL.");
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        await loginWithGoogleSession(sessionId);
        // Clean the hash so refreshes don't re-process
        try {
          const url = new URL(window.location.href);
          url.hash = "";
          window.history.replaceState({}, "", url.toString());
        } catch (e) { console.debug("hash clean", e); }
        // AuthProvider state has been updated; App will re-render the dashboard.
      } catch (err) {
        setStatus("error");
        setErrMsg(err.response?.data?.detail || err.message || "Sign-in failed.");
      }
    })();
  }, [loginWithGoogleSession]);

  if (status === "error") {
    return (
      <div className="loading-screen" data-testid="auth-callback-error">
        <div className="loading-logo">!</div>
        <div className="loading-text">SIGN-IN ERROR</div>
        <div className="loading-sub" style={{ maxWidth: 360, textAlign: "center" }}>{errMsg}</div>
        <button
          className="btn btn--accent"
          style={{ marginTop: 16 }}
          onClick={() => { window.location.href = window.location.origin + "/"; }}
          data-testid="auth-callback-retry"
        >
          Back to sign-in
        </button>
      </div>
    );
  }
  return (
    <div className="loading-screen" data-testid="auth-callback-processing">
      <div className="loading-logo">VA</div>
      <div className="loading-text">CLAIMOPS VA</div>
      <div className="loading-sub">Signing you in with Google...</div>
    </div>
  );
}
